import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { ParametersService, ParamType } from '@app/core/parameters';
import {
  IAcumuladosRow,
  IPatientHistory,
  PatientRequestsService,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { MsgboxService } from '@app/shared/msgbox';
import { formatPatientRequestDateTime } from '../patient-request-date-format.util';
import {
  buildAcumuladosCsvFilename,
  buildAcumuladosTableCsv,
} from './acumulados-table-csv';

export interface AcumuladosRequestColumn {
  requisCol: number;
  requisNr: string;
  extRequisNr: string;
  requisSite: string; // RequisSite de wspi_Search_Cumu para abrir detalle con el sitio correcto (#506)
  entryDate: string;   // formatted for display
  rawEntryDate: string; // original SP value, used to open the request detail
  searchReferenceDate: string; // dd/MM/yyyy HH:mm for wspi_Search_Cumu navigation
  isOrigin?: boolean;
}

export interface AcumuladosTestRow {
  testRow: number;
  testName: string;
  units: string;
  printSeq: number;
  isTitle: boolean; // ResType 10, 11, 12
  hasAnyResult: boolean;
  cells: { requisCol: number; result: string | number; pathology: number; normalRange?: string }[];
}

@Component({
  selector: 'afoone-acumulados-table-view',
  templateUrl: './acumulados-table-view.component.html',
  styleUrls: ['./acumulados-table-view.component.css'],
})
export class AcumuladosTableViewComponent implements OnInit {
  patient: IPatientHistory | null = null;
  requestColumns: AcumuladosRequestColumn[] = [];
  /** Reparto de anchos al 100%: CSS `calc(... / var(--acum-requis-cols))`. */
  get acumRequisColsVar(): number {
    return Math.max(this.requestColumns.length, 1);
  }
  testRows: AcumuladosTestRow[] = [];
  loading = true;
  error: string | null = null;
  messageInfo: string | null = null; // CUMULATIVE_* no modal
  /**
   * Tras al menos una tabla con columnas, se muestran acciones de navegación aunque un intento
   * devuelva vacío + mensaje (#502: seguir pudiendo pulsar el otro sentido).
   */
  acumuladosNavEnabled = false;
  /** Anclas de fecha/hora (dd/MM/yyyy HH:mm) del pivot actual; respaldo si la tabla se vacía. */
  private navRefNewest = '';
  private navRefOldest = '';
  private emptyStateVerAnterioresRef = '';
  private emptyStateVerPosterioresRef = '';

  // Search params for Ver anteriores / posteriores
  private patientId = '';
  private labNr = 0;
  private referenceDate = '';
  private requisType = 0;
  private testList = '';
  private originRequisNr = ''; // petición origen (highlight header)
  // Params de la petición origen para enlace directo "Ver resultados petición"
  private originLaboratorio = '';
  private originPeticion = '';
  private originLabor = 0;
  private originDate = '';
  private originImpreso = '.';
  private originSitio = 'D';
  private centroId: number | null = null;

  /** Colores de patología desde WCONFIGURATION: parámetro 12 (patológico), 13 (alarma), 14 (extremo). */
  pathologyColors: string[] = []; // [patológico, alarma, extremo]

  /** Muestra «Ver posteriores / Ver anteriores» (#502: no solo cuando hay columnas en DOM). */
  get showAcumuladosNav(): boolean {
    return this.acumuladosNavEnabled && !!this.patient;
  }

  get canExportCsv(): boolean {
    return !!this.patient && this.requestColumns.length > 0 && this.testRows.length > 0 && !this.messageInfo;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: PatientRequestsService,
    private parametersService: ParametersService,
    private keycloak: KeycloakService,
    private userService: UsersService,
    private msgbox: MsgboxService
  ) {}

  /** Carga colores de patología desde WCONFIGURATION (parámetros 12, 13, 14). */
  private loadPathologyColors(): void {
    this.parametersService
      .getParameters([
        ParamType.ColorPatologico,  // 12: color patológico (hex RGB)
        ParamType.ColorAlarma,      // 13: color alarma
        ParamType.ColorExtremo,     // 14: color extremo
      ])
      .subscribe((p) => {
        const byType = new Map(p.map((prm) => [prm.paramType, prm.value]));
        this.pathologyColors = [
          byType.get(ParamType.ColorPatologico) ?? '',
          byType.get(ParamType.ColorAlarma) ?? '',
          byType.get(ParamType.ColorExtremo) ?? '',
        ];
      });
  }

  /** Espera a que haya token (pestaña nueva puede tardar en tener sesión). */
  private async waitForToken(maxWaitMs = 5000): Promise<boolean> {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const token = await this.keycloak.getToken();
      if (token) return true;
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(async (qp) => {
      this.patientId = qp['patientId'] ?? '';
      this.labNr = Number.parseInt(qp['labNr'] ?? '0', 10);
      this.referenceDate = qp['referenceDate'] ?? '';
      this.requisType = Number.parseInt(qp['requisType'] ?? '0', 10);
      this.testList = qp['testList'] ?? '';
      this.originRequisNr = qp['originRequisNr'] ?? '';
      this.originLaboratorio = qp['originLaboratorio'] ?? '';
      this.originPeticion = qp['originPeticion'] ?? '';
      this.originLabor = Number.parseInt(qp['originLabor'] ?? '0', 10);
      this.originDate = qp['originDate'] ?? '';
      this.originImpreso = qp['originImpreso'] ?? '.';
      this.originSitio = qp['originSitio'] ?? 'D';
      const centroIdParam = qp['centroId'];
      if (centroIdParam != null && centroIdParam !== '') {
        const c = Number(centroIdParam);
        if (!Number.isNaN(c)) {
          this.centroId = c;
          this.userService.setSisifoDefaultCenter(c);
        }
      } else {
        this.centroId = null;
      }
      if (!this.patientId) {
        this.loading = false;
        this.error = 'Faltan parámetros (patientId).';
        return;
      }
      const hasToken = await this.waitForToken();
      if (!hasToken) {
        this.loading = false;
        this.error = 'Sesión no disponible. Cierra esta pestaña e intenta de nuevo desde la pestaña original.';
        return;
      }
      this.loadPathologyColors();
      this.loadData();
    });
  }

  private loadPatient(): Promise<IPatientHistory | null> {
    return new Promise((resolve) => {
      this.service
        .getPatientHistories({
          history: this.patientId.trim(),
          searchType: 'C',
          centroId: this.currentCenterId(),
        } as any)
        .subscribe({
          next: (list) => {
            const normalizedId = this.patientId.trim().replace(/^\*/, '');
            const p = list?.find((x) => {
              const h = (x.historia?.trim() ?? '').replace(/^\*/, '');
              return h === normalizedId || (x.historia?.trim() ?? '') === this.patientId.trim();
            });
            resolve(p ?? null);
          },
          error: (err) => {
            const key = err?.error?.message || err?.message;
            if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
              this.msgbox.showError(key);
            } else {
              this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
            }
            resolve(null);
          },
        });
    });
  }

  private loadAcumulados(searchDirection?: number, referenceDateOverride?: string): Promise<{ data: IAcumuladosRow[]; msgCode?: string; msgType?: number }> {
    const ref = referenceDateOverride ?? this.referenceDate;
    const params: any = {
      patientId: this.patientId,
      labNr: this.labNr,
      referenceDate: ref,
      requisType: this.requisType,
      testList: this.testList || undefined,
      graphicFormat: 0,
      centroId: this.currentCenterId(),
    };
    if (searchDirection != null) params.searchDirection = searchDirection;
    return new Promise((resolve, reject) => {
      this.service.searchAcumulados(params).subscribe({
        next: (res) => {
          const data = Array.isArray(res) ? res : (res as { data?: IAcumuladosRow[] })?.data ?? [];
          const msgCode = (res as { msgCode?: string })?.msgCode;
          const msgType = (res as { msgType?: number })?.msgType;
          resolve({
            data: (data as IAcumuladosRow[]) || [],
            msgCode: msgCode?.trim() || undefined,
            msgType: msgType == null ? 0 : Number(msgType),
          });
        },
        error: (err) => reject(err),
      });
    });
  }

  private buildPivot(rows: IAcumuladosRow[]): void {
    const get = (r: Record<string, unknown>, pascal: string): unknown =>
      r[pascal] ?? r[pascal.charAt(0).toLowerCase() + pascal.slice(1)] ?? r[pascal.toLowerCase()];
    const text = (value: unknown): string => {
      if (value == null) return '';
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
      return '';
    };
    const reqMap = new Map<number, { requisNr: string; extRequisNr: string; requisSite: string; entryDate: string; rawEntryDate: string; searchReferenceDate: string }>();
    const testRowMap = new Map<
      number,
      { testName: string; units: string; printSeq: number; isTitle: boolean }
    >();
    const cellMap = new Map<string, { result: string | number; pathology: number; normalRange?: string }>();
    // Cuando el SP devuelve RequisCol=0 (p. ej. en "anteriores"), derivar columna por (RequisNr, EntryDate)
    let syntheticColIndex = 0;
    const colKeyToIndex = new Map<string, number>();

    for (const r of rows) {
      const row = r as unknown as Record<string, unknown>;
      const requisColFromSp = Number(get(row, 'RequisCol') ?? 0);
      // Solo crear columnas para peticiones reales (RequisCol > 0); ignorar RequisCol=0 del SP
      if (requisColFromSp <= 0) continue;

      const rn = text(get(row, 'RequisNr')).trim();
      const rawEntry = text(get(row, 'EntryDate')).trim();
      const colKey = `${rn}|${rawEntry}`;
      if (!colKeyToIndex.has(colKey)) {
        syntheticColIndex += 1;
        colKeyToIndex.set(colKey, syntheticColIndex);
      }
      const col = colKeyToIndex.get(colKey) ?? syntheticColIndex;
      if (col > 0) {
        const previous = reqMap.get(col);
        const raw = text(get(row, 'EntryDate'));
        const formattedEntryDate = this.formatEntryDate(raw);
        reqMap.set(col, {
          requisNr: text(get(row, 'RequisNr')),
          extRequisNr: text(get(row, 'ExtRequisNr')),
          requisSite: text(get(row, 'RequisSite')).trim() || previous?.requisSite || '',
          entryDate: formattedEntryDate,
          rawEntryDate: raw,
          searchReferenceDate: formattedEntryDate || raw,
        });
      }
      const testRow = Number(get(row, 'TestRow') ?? 0);
      const key = `${testRow}-${col}`;
      if (!testRowMap.has(testRow)) {
        testRowMap.set(testRow, {
          testName: text(get(row, 'TestName')),
          units: text(get(row, 'Units')),
          printSeq: Number(get(row, 'PrintSeq') ?? 0),
          isTitle: [10, 11, 12].includes(Number(get(row, 'ResType'))),
        });
      }
      const resType = Number(get(row, 'ResType'));
      if (resType !== 10 && resType !== 11 && resType !== 12) {
        cellMap.set(key, {
          result: (get(row, 'Result') as string | number) ?? '',
          pathology: Number(get(row, 'Pathology') ?? 0),
          normalRange: get(row, 'NormalRange') as string | undefined,
        });
      }
    }

    const cols = Array.from(reqMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([requisCol, v]) => ({
        requisCol,
        requisNr: v.requisNr,
        extRequisNr: v.extRequisNr,
        requisSite: v.requisSite,
        entryDate: v.entryDate,
        rawEntryDate: v.rawEntryDate,
        searchReferenceDate: v.searchReferenceDate,
        isOrigin: this.isOriginColumn(v.requisNr, v.rawEntryDate),
      }));
    this.requestColumns = cols;
    if (cols.length) {
      this.navRefNewest = cols[0].searchReferenceDate;
      this.navRefOldest = cols.at(-1)?.searchReferenceDate ?? '';
      this.emptyStateVerAnterioresRef = '';
      this.emptyStateVerPosterioresRef = '';
      this.acumuladosNavEnabled = true;
    }

    const testRowsSorted = Array.from(testRowMap.entries()).sort(
      (a, b) => (a[1].printSeq || a[0]) - (b[1].printSeq || b[0])
    );
    this.testRows = testRowsSorted.map(([testRow, info]) => {
      const cells = cols.map((c) => {
        const cell = cellMap.get(`${testRow}-${c.requisCol}`);
        return {
          requisCol: c.requisCol,
          result: cell?.result ?? '',
          pathology: cell?.pathology ?? 0,
          normalRange: cell?.normalRange,
        };
      });
      const hasAnyResult = cells.some((c) => c.result !== '' && c.result != null);
      return {
        testRow,
        testName: info.testName,
        units: info.units,
        printSeq: info.printSeq,
        isTitle: info.isTitle,
        hasAnyResult,
        cells,
      };
    });
  }

  private detailLaboratoryForRequestLink(): string {
    const explicit = String(this.originLaboratorio ?? '').trim();
    if (explicit) return explicit;
    const lab = this.originLabor || this.labNr;
    return lab === 2 ? 'MIC' : 'GEN';
  }

  private currentCenterId(): number | null {
    return this.centroId ?? this.userService.getSisifoDefaultCenter();
  }

  /** Indica si esta columna es la petición origen (desde la que se abrió Acumulados). */
  private isOriginColumn(requisNr: string, rawEntryDate: string): boolean {
    if (!this.originRequisNr) return false;
    const a = String(requisNr ?? '').trim();
    const b = String(this.originRequisNr ?? '').trim();
    if (a === b) return true;
    const na = Number(a);
    const nb = Number(b);
    if (a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb) && na === nb) return true;
    return false;
  }

  private formatEntryDate(s: string): string {
    return formatPatientRequestDateTime(s);
  }

  cellTooltipText(result: string | number | null | undefined, normalRange?: string): string {
    const text = result == null ? '' : String(result).trim();
    const range = normalRange == null ? '' : String(normalRange).trim();
    if (range) {
      return text ? `${text}\nRango normalidad: ${range}` : `Rango normalidad: ${range}`;
    }
    return text;
  }

  exportCsv(): void {
    if (!this.canExportCsv) return;
    const csvBody = buildAcumuladosTableCsv(this.requestColumns, this.testRows);
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvBody], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = buildAcumuladosCsvFilename(this.patient?.historia, this.requestColumns);
    a.click();
    URL.revokeObjectURL(url);
  }

  private referenceDateAtDayStart(value: string, daysToAdd = 0): string {
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(value ?? '').trim());
    if (!match) return value;
    const [, day, month, year] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day) + daysToAdd);
    const nextDay = String(date.getDate()).padStart(2, '0');
    const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
    return `${nextDay}/${nextMonth}/${date.getFullYear()} 00:00`;
  }

  /** WIR-RF-03.01.6.14: si el SP indica msgType > 0, mostrar msgCode traducido y no la tabla. */
  private applySpMessageIfAny(response: { msgCode?: string; msgType?: number }): boolean {
    if (Number(response.msgType) > 0 && response.msgCode?.trim()) {
      this.requestColumns = [];
      this.testRows = [];
      this.messageInfo = response.msgCode.trim();
      return true;
    }
    return false;
  }

  private async loadData(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.messageInfo = null;
    this.acumuladosNavEnabled = false;
    this.navRefNewest = '';
    this.navRefOldest = '';
    this.emptyStateVerAnterioresRef = '';
    this.emptyStateVerPosterioresRef = '';
    try {
      const [patient, response] = await Promise.all([
        this.loadPatient(),
        this.loadAcumulados(),
      ]);
      this.patient = patient ?? null;
      if (this.applySpMessageIfAny(response)) {
        this.loading = false;
        return;
      }
      const rows = response.data;
      if (!rows?.length) {
        this.requestColumns = [];
        this.testRows = [];
        this.messageInfo =
          response.msgCode && response.msgType !== 0 ? response.msgCode : 'CUMULATIVE_NOT_FOUND';
        this.loading = false;
        return;
      }
      this.buildPivot(rows);
    } catch (err: any) {
      this.error = err?.message || err?.error?.message || 'Error al cargar acumulados';
    }
    this.loading = false;
  }

  /**
   * Color del resultado según Pathology (wspi_Search_Cumu):
   * 1 o 2 → patológico (fuera rango normalidad; 1=por debajo, 2=por encima) → WCONFIGURATION param 12
   * 3 o 4 → alarma (fuera rango alarma; 3=por debajo, 4=por encima) → param 13
   * 5 o 6 → extremo → param 14
   */
  getPathologyColor(pathology: number): string {
    if (!pathology || !this.pathologyColors?.length) return '';
    if (pathology === 1 || pathology === 2) return this.pathologyColors[0] ?? '';
    if (pathology === 3 || pathology === 4) return this.pathologyColors[1] ?? '';
    if (pathology === 5 || pathology === 6) return this.pathologyColors[2] ?? '';
    return '';
  }

  verAnteriores(): void {
    const refDate = this.emptyStateVerAnterioresRef || (this.requestColumns.length
      ? this.requestColumns.at(-1)?.searchReferenceDate
      : this.navRefOldest);
    if (!refDate) return;
    this.messageInfo = null;
    this.loading = true;
    this.loadAcumulados(2, refDate)
      .then((response) => {
        this.loading = false;
        if (!response.data?.length) {
          this.requestColumns = [];
          this.testRows = [];
          this.emptyStateVerPosterioresRef = this.referenceDateAtDayStart(refDate);
          this.emptyStateVerAnterioresRef = '';
          this.messageInfo =
            response.msgCode && response.msgType !== 0 ? response.msgCode : 'CUMULATIVE_NEXT_NOT_FOUND';
          return;
        }
        if (this.applySpMessageIfAny(response)) return;
        this.buildPivot(response.data);
      })
      .catch(() => {
        this.loading = false;
        this.messageInfo = 'CUMULATIVE_NEXT_NOT_FOUND';
      });
  }

  verPosteriores(): void {
    const refDate = this.emptyStateVerPosterioresRef || (this.requestColumns.length
      ? this.requestColumns[0].searchReferenceDate
      : this.navRefNewest);
    if (!refDate) return;
    this.messageInfo = null;
    this.loading = true;
    this.loadAcumulados(1, refDate)
      .then((response) => {
        this.loading = false;
        if (!response.data?.length) {
          this.requestColumns = [];
          this.testRows = [];
          this.emptyStateVerAnterioresRef = this.referenceDateAtDayStart(refDate, 1);
          this.emptyStateVerPosterioresRef = '';
          this.messageInfo =
            response.msgCode && response.msgType !== 0 ? response.msgCode : 'CUMULATIVE_PREV_NOT_FOUND';
          return;
        }
        if (this.applySpMessageIfAny(response)) return;
        this.buildPivot(response.data);
      })
      .catch(() => {
        this.loading = false;
        this.messageInfo = 'CUMULATIVE_PREV_NOT_FOUND';
      });
  }

  openRequestResults(col: AcumuladosRequestColumn): void {
    const detailLaboratory = this.detailLaboratoryForRequestLink();
    // Abrir "Ver resultados petición"; si no llega laboratorio origen, inferirlo desde el labNr de acumulados.
    if (detailLaboratory && col.rawEntryDate) {
      const centroId = this.currentCenterId();
      const q: Record<string, string | number | undefined> = {
        laboratorio: detailLaboratory,
        peticion: (col.requisNr ?? '').toString().trim(),
        labor: this.originLabor || this.labNr,
        date: col.rawEntryDate,
        impreso: this.originImpreso,
        sitio: col.requisSite || this.originSitio || 'D',
        fromAcumulados: 1,
      };
      if (centroId != null) q['centroId'] = centroId;
      const tree = this.router.createUrlTree(['/patient-requests', 'view-request'], {
        queryParams: q,
        queryParamsHandling: '',
      });
      const url = this.router.serializeUrl(tree);
      const full = globalThis.location.origin + (url.startsWith('/') ? url : '/' + url);
      globalThis.open(full, '_blank');
      return;
    }
    // Sin fecha de la columna no se puede construir el detalle, así que volvemos a la lista del paciente.
    const centroId = this.currentCenterId();
    const q: Record<string, string | number | undefined> = {};
    if (centroId != null) q['centroId'] = centroId;
    const tree = this.router.createUrlTree(['/patient-requests', 'patient', this.patientId], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const full = globalThis.location.origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open(full, '_blank');
  }
}
